import {
  computed,
  onScopeDispose,
  reactive,
  ref,
  shallowRef,
  watch,
} from 'vue';
import { useNow } from '@vueuse/core';
import { ReservationsService } from '@/api/services/reservations.service';
import type {
  ICreateReservationDto,
  IUpdateReservationDto,
} from '@/api/services/reservations.service';
import type {
  IReservationFormProps,
  IReservationGuestDraft,
  IReservationAlternative,
} from '@/components/v2/reservations/reservation-form.interfaces';
import type { IReservationTablePickerInventory } from '@/interfaces/IReservationUi';
import {
  addDays,
  defaultBoardDate,
  toDateKey,
} from '@/domain/reservations/reservation-time';
import {
  earliestReservationServiceDate,
  isUpcomingReservationStart,
} from '@/domain/reservations/reservation-service-date';
import { getServerErrorCode } from '@/domain/reservations/reservation-errors';
import { normalizeReservationPhone } from '@/domain/reservations/reservation-phone';
import { getReservationSlotState } from '@/domain/reservations/reservation-slots';
import {
  computeTableAvailability,
  requiresForcedTableSelection,
} from '@/domain/reservations/table-availability';
import type { IPosTableView } from '@/interfaces/IPosTableView';
import type {
  IReservationDetail,
  TReservationSource,
} from '@/interfaces/IReservation';
import type {
  IReservationSlot,
  IReservationSlots,
} from '@/interfaces/IReservationSlots';
import type { IReservationBookingContext } from '@/interfaces/IReservationSettings';

interface IFormContext {
  restaurantId: number;
  branchId: number;
  country: string;
  timeZone: string;
  businessDayStartHour?: number;
  tables: IPosTableView[];
}
export interface IReservationFormDraft {
  partySize: number;
  date: string;
  startsAt: string;
  guest: IReservationGuestDraft;
  tableIds: number[];
  manualTables: boolean;
  duration: string;
  sector: string;
  source: TReservationSource;
  notesGuest: string;
  notesInternal: string;
  allergies: string;
  occasion: string;
  tags: string[];
  notifyGuest: boolean;
  collectDeposit: boolean;
  force: boolean;
}
function record(value: unknown): value is Record<string, unknown> {
  return !!value && typeof value === 'object' && !Array.isArray(value);
}
export function reservationConflictAlternatives(
  error: unknown
): IReservationAlternative[] {
  if (
    !record(error) ||
    !record(error.response) ||
    !record(error.response.data) ||
    !Array.isArray(error.response.data.alternatives)
  )
    return [];
  return error.response.data.alternatives
    .filter(
      (item): item is IReservationAlternative =>
        record(item) &&
        typeof item.startsAt === 'string' &&
        /T.*(?:Z|[+-]\d{2}:\d{2})$/.test(item.startsAt) &&
        Number.isFinite(Date.parse(item.startsAt)) &&
        typeof item.label === 'string'
    )
    .sort((a, b) => Date.parse(a.startsAt) - Date.parse(b.startsAt))
    .slice(0, 4);
}
export function useReservationForm(
  props: IReservationFormProps,
  options: {
    context: () => IFormContext;
    saved: (row: IReservationDetail, deposit: boolean) => void;
    error: (cause: unknown) => void;
  }
) {
  const initial = { ...options.context() };
  const edit = props.reservationToEdit;
  const draft = reactive<IReservationFormDraft>({
    partySize: edit?.partySize ?? props.partySize ?? 2,
    date:
      edit?.serviceDate ??
      props.date ??
      (props.startsAt
        ? toDateKey(props.startsAt, initial.timeZone)
        : defaultBoardDate(
            new Date(),
            initial.timeZone,
            initial.businessDayStartHour
          )),
    startsAt: edit?.startsAt ?? props.startsAt ?? '',
    guest: {
      customerId: edit?.customerId ?? props.guest?.customerId ?? null,
      name: edit?.guestName ?? props.guest?.name ?? '',
      phone: edit?.guestPhone ?? props.guest?.phoneE164 ?? '',
      email: edit?.guestEmail ?? props.guest?.email ?? '',
    },
    tableIds: [...(edit?.tableIds ?? props.tableIds ?? [])],
    manualTables: !!(edit?.tableIds.length || props.tableIds?.length),
    duration: edit ? String(edit.durationMin) : '',
    sector: edit?.sectorId ? String(edit.sectorId) : '',
    source: edit?.source ?? 'staff',
    notesGuest: edit?.notesGuest ?? '',
    notesInternal: edit?.notesInternal ?? '',
    allergies: edit?.allergies ?? '',
    occasion: edit?.occasion ?? '',
    tags: [...(edit?.tags ?? [])],
    notifyGuest: true,
    collectDeposit: true,
    force: false,
  });
  const now = useNow({ interval: 60000 });
  const previousDayWindowEnds = shallowRef<string[]>([]);
  const minimumDate = computed(() =>
    earliestReservationServiceDate(
      now.value,
      initial.timeZone,
      initial.businessDayStartHour,
      previousDayWindowEnds.value
    )
  );
  const slots = shallowRef<IReservationSlots | null>(null);
  const inventory = shallowRef<IReservationTablePickerInventory | null>(null);
  const configuration = shallowRef<IReservationBookingContext | null>(null);
  const loading = ref(false);
  const failed = ref(false);
  const saving = ref(false);
  const errors = ref<Record<string, string>>({});
  const conflict = ref(false);
  const alternatives = shallowRef<IReservationAlternative[]>([]);
  const sourceChanged = ref(false);
  let alive = true;
  let sequence = 0;
  const current = () =>
    alive &&
    initial.restaurantId === options.context().restaurantId &&
    initial.branchId === options.context().branchId;
  onScopeDispose(() => {
    alive = false;
    sequence++;
  });
  const durationMin = computed(() =>
    draft.duration ? Number(draft.duration) : slots.value?.durationMin ?? 90
  );
  const selectedSlot = computed(
    () =>
      slots.value?.slots.find(
        (slot) => Date.parse(slot.startsAt) === Date.parse(draft.startsAt)
      ) ?? null
  );
  const explicitIds = computed(() =>
    draft.manualTables ? draft.tableIds : []
  );
  const slotStates = computed(() =>
    Object.fromEntries(
      (slots.value?.slots ?? []).map((slot) => {
        const tables = inventory.value
          ? computeTableAvailability({
              tables: options.context().tables,
              reservations: inventory.value.reservations,
              startsAt: slot.startsAt,
              durationMin: durationMin.value,
              partySize: draft.partySize,
              bufferMin: inventory.value.settings.bufferMin,
              now: new Date(),
              excludeReservationId: edit?.id,
            })
          : [];
        const state = getReservationSlotState(slot, {
          force: draft.force && slot.startsAt === draft.startsAt,
          explicitTableIds: explicitIds.value,
          tables,
        });
        const explicitValid =
          !explicitIds.value.length ||
          explicitIds.value.every((id) =>
            tables.some((table) => table.table.id === id && table.selectable)
          );
        const hasInventory = explicitIds.value.length
          ? explicitValid
          : !!slot.unit &&
            slot.unit.tableIds.every((id) =>
              tables.some(
                (table) =>
                  table.table.id === id &&
                  table.selectable &&
                  table.status !== 'reserved'
              )
            );
        return [
          slot.startsAt,
          {
            ...state,
            selectable:
              state.selectable &&
              slot.durationMin === durationMin.value &&
              hasInventory &&
              isUpcomingReservationStart(
                slot.startsAt,
                now.value,
                edit?.startsAt
              ),
            canForce:
              state.canForce &&
              slot.durationMin === durationMin.value &&
              isUpcomingReservationStart(
                slot.startsAt,
                now.value,
                edit?.startsAt
              ) &&
              ['pacing', 'window_full'].includes(slot.reason ?? ''),
          },
        ];
      })
    )
  );
  const forcePrompt = computed(
    () =>
      !!selectedSlot.value &&
      ['pacing', 'window_full'].includes(selectedSlot.value.reason ?? '') &&
      selectedSlot.value.forceable
  );
  const depositRequired = computed(
    () =>
      !edit &&
      !loading.value &&
      !failed.value &&
      slots.value?.date === draft.date &&
      selectedSlot.value?.depositRequired === true
  );
  const depositAmount = computed(() =>
    edit
      ? edit.depositAmount
      : depositRequired.value
      ? selectedSlot.value?.depositAmount ?? null
      : null
  );
  const collectingDeposit = computed(
    () => depositRequired.value && draft.collectDeposit
  );
  const dateValid = computed(() => {
    try {
      return (
        /^\d{4}-\d{2}-\d{2}$/.test(draft.date) &&
        toDateKey(draft.date, initial.timeZone) >= minimumDate.value
      );
    } catch {
      return false;
    }
  });

  async function load(preserveConflict = false) {
    const request = ++sequence;
    const customDuration = draft.duration ? Number(draft.duration) : undefined;
    let canLoadDate = false;
    try {
      canLoadDate =
        toDateKey(draft.date, initial.timeZone) >=
        addDays(toDateKey(now.value, initial.timeZone), -1);
    } catch {
      /* The date input can be incomplete while typing. */
    }
    if (
      !current() ||
      !canLoadDate ||
      (customDuration !== undefined &&
        (!Number.isInteger(customDuration) ||
          customDuration < 30 ||
          customDuration > 360)) ||
      !Number.isInteger(draft.partySize) ||
      draft.partySize < 1 ||
      draft.partySize > 50
    ) {
      slots.value = null;
      inventory.value = null;
      loading.value = false;
      return;
    }
    const date = draft.date;
    const party = draft.partySize;
    const previousDate = addDays(toDateKey(now.value, initial.timeZone), -1);
    const dayDates = [
      ...new Set([
        ...[-2, -1, 0, 1, 2].map((offset) => addDays(date, offset)),
        previousDate,
      ]),
    ];
    loading.value = true;
    failed.value = false;
    inventory.value = null;
    if (!preserveConflict) {
      conflict.value = false;
      alternatives.value = [];
    }
    try {
      const [payload, days, config] = await Promise.all([
        ReservationsService.getSlots(
          initial.restaurantId,
          initial.branchId,
          date,
          party,
          {
            ...(edit ? { excludeReservationId: edit.id } : {}),
            ...(customDuration !== undefined
              ? { durationMin: customDuration }
              : {}),
          }
        ),
        Promise.all(
          dayDates.map((serviceDate) =>
            ReservationsService.getDay(
              initial.restaurantId,
              initial.branchId,
              serviceDate
            )
          )
        ),
        configuration.value
          ? Promise.resolve(configuration.value)
          : ReservationsService.getBookingContext(
              initial.restaurantId,
              initial.branchId
            ),
      ]);
      if (!current() || request !== sequence) return;
      const selectedDay = days.find((day) => day.date === date);
      if (!selectedDay)
        throw new Error(
          'Reservation day response does not match the requested date'
        );
      previousDayWindowEnds.value =
        days
          .find((day) => day.date === previousDate)
          ?.shifts.map((shift) => shift.endsAt) ?? [];
      slots.value = payload;
      configuration.value = config;
      inventory.value = {
        reservations: [
          ...new Map(
            days.flatMap((day) => day.reservations).map((row) => [row.id, row])
          ).values(),
        ],
        settings: selectedDay.settingsSummary,
        bookingContext: config,
      };
      loading.value = false;
    } catch {
      if (!current() || request !== sequence) return;
      loading.value = false;
      failed.value = true;
    }
  }
  watch(
    () => [
      draft.date,
      draft.partySize,
      draft.duration,
      options.context().restaurantId,
      options.context().branchId,
    ],
    () => {
      draft.force = false;
      void load();
    },
    { immediate: true }
  );
  function chooseSlot(startsAt: string) {
    draft.startsAt = startsAt;
    draft.force = false;
    conflict.value = false;
    alternatives.value = [];
    errors.value = { ...errors.value, time: '' };
  }
  function requestForce(slot: IReservationSlot) {
    draft.startsAt = slot.startsAt;
    draft.force = false;
  }
  function validate() {
    const result: Record<string, string> = {};
    if (
      !Number.isInteger(draft.partySize) ||
      draft.partySize < 1 ||
      draft.partySize > 50
    )
      result.party = 'reservations.form.partyInvalid';
    if (!dateValid.value) result.date = 'reservations.form.pastDate';
    const selected = selectedSlot.value;
    if (
      loading.value ||
      failed.value ||
      !selected ||
      !isUpcomingReservationStart(
        selected.startsAt,
        new Date(),
        edit?.startsAt
      ) ||
      !slotStates.value[selected.startsAt]?.selectable
    )
      result.time = 'reservations.form.selectTime';
    if (
      draft.manualTables &&
      (!draft.tableIds.length || draft.tableIds.length > 6)
    )
      result.tables = 'reservations.form.tablePick';
    if (
      draft.guest.name.trim().length < 2 ||
      draft.guest.name.trim().length > 128
    )
      result.name = 'reservations.form.nameRequired';
    if (
      draft.guest.phone.trim() &&
      !normalizeReservationPhone(draft.guest.phone, initial.country)
    )
      result.phone = 'reservations.form.phoneInvalid';
    if (
      draft.guest.email.trim() &&
      (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(draft.guest.email.trim()) ||
        draft.guest.email.trim().length > 128)
    )
      result.email = 'reservations.form.emailInvalid';
    if (
      collectingDeposit.value &&
      !draft.guest.phone.trim() &&
      !draft.guest.email.trim()
    )
      result.phone = 'reservations.form.depositNeedsContact';
    errors.value = result;
    return Object.keys(result).length === 0;
  }
  function explicitForce() {
    if (!explicitIds.value.length || !inventory.value) return false;
    const choices = computeTableAvailability({
      tables: options.context().tables,
      reservations: inventory.value.reservations,
      startsAt: draft.startsAt,
      durationMin: durationMin.value,
      partySize: draft.partySize,
      bufferMin: inventory.value.settings.bufferMin,
      now: new Date(),
      excludeReservationId: edit?.id,
    }).filter((table) => explicitIds.value.includes(table.table.id));
    return requiresForcedTableSelection(
      choices,
      draft.partySize,
      configuration.value?.tableGroups ?? []
    );
  }
  async function submit(): Promise<IReservationDetail | null> {
    if (!current() || saving.value || !validate()) return null;
    saving.value = true;
    const collectDeposit = collectingDeposit.value;
    const dto: IUpdateReservationDto = {
      partySize: draft.partySize,
      startsAt: selectedSlot.value?.startsAt ?? draft.startsAt,
      guestName: draft.guest.name.trim(),
      guestPhone: draft.guest.phone.trim()
        ? normalizeReservationPhone(draft.guest.phone, initial.country)
        : null,
      guestEmail: draft.guest.email.trim() || null,
      customerId: draft.guest.customerId,
      sectorId: draft.sector ? Number(draft.sector) : null,
      ...(draft.manualTables ? { tableIds: [...draft.tableIds] } : {}),
      durationMin: durationMin.value,
      notesGuest: draft.notesGuest.trim() || null,
      notesInternal: draft.notesInternal.trim() || null,
      allergies: draft.allergies.trim() || null,
      occasion: draft.occasion || null,
      tags: draft.tags.length ? [...draft.tags] : null,
      ...((!edit || sourceChanged.value) &&
      (draft.source === 'phone' || draft.source === 'staff')
        ? { source: draft.source }
        : {}),
      force: draft.force || explicitForce(),
    };
    try {
      let updated: IReservationDetail;
      if (edit)
        updated = await ReservationsService.update(edit.id, {
          ...dto,
          notifyGuest: draft.notifyGuest,
        });
      else {
        const create: ICreateReservationDto = {
          ...dto,
          collectDeposit,
          restaurantId: initial.restaurantId,
          branchId: initial.branchId,
          guestName: draft.guest.name.trim(),
          partySize: draft.partySize,
          startsAt: selectedSlot.value?.startsAt ?? draft.startsAt,
        };
        updated = (await ReservationsService.create(create)).reservation;
      }
      if (!current()) return null;
      saving.value = false;
      options.saved(updated, collectDeposit);
      return updated;
    } catch (cause) {
      if (!current()) return null;
      saving.value = false;
      if (getServerErrorCode(cause) === 'SLOT_UNAVAILABLE') {
        conflict.value = true;
        alternatives.value = reservationConflictAlternatives(cause);
        errors.value = { time: 'reservations.form.conflictTitle' };
        void load(true);
      } else options.error(cause);
      return null;
    }
  }
  return {
    draft,
    slots,
    inventory,
    configuration,
    loading,
    failed,
    saving,
    errors,
    conflict,
    alternatives,
    sourceChanged,
    durationMin,
    selectedSlot,
    slotStates,
    forcePrompt,
    depositRequired,
    depositAmount,
    collectingDeposit,
    dateValid,
    minimumDate,
    load,
    chooseSlot,
    requestForce,
    validate,
    submit,
  };
}
